import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Badge, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { listMessageLogs, sendConversationMessage, downloadMessageMedia } from "../../../../../services/WhatsappService";
import { formatDateTime, formatTimeAgo } from "../../../../../utils/DateUtils";
import { formatPhoneNumberOfPhoneComponent } from "../../../../../utils/FormatUtils";
import { getMessageBody, getPayloadObject } from "../../../../../utils/WhatsappMessageUtils";
import PageTitle from "../../../../layouts/PageTitle";
import { errorAlert } from "../../../../components/alert/defaultAlert";

const AudioMessagePlayer = ({ log, t }) => {
    const [audioSrc, setAudioSrc] = useState(null);
    const [audioError, setAudioError] = useState(false);

    useEffect(() => {
        let objectUrl = null;
        let mounted = true;

        downloadMessageMedia(log.id)
            .then((response) => {
                objectUrl = URL.createObjectURL(response.data);
                if (mounted) {
                    setAudioSrc(objectUrl);
                }
            })
            .catch(() => {
                if (mounted) {
                    setAudioError(true);
                }
            });

        return () => {
            mounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [log.id]);

    if (audioError) {
        return (
            <div className="message-audio unavailable">
                <i className="fa fa-microphone-slash"></i>
                <span className="audio-duration">{t('audio_message')}</span>
            </div>
        );
    }

    if (!audioSrc) {
        return (
            <div className="message-audio loading">
                <i className="fa fa-spinner fa-spin"></i>
                <span className="audio-duration">{t('loading')}...</span>
            </div>
        );
    }

    return (
        <div className="message-audio-player">
            <audio controls preload="metadata" src={audioSrc}>
                {t('audio_message')}
            </audio>
        </div>
    );
};

const WhatsappMessageLogList = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const conversationId = location.state?.conversationId;
    const customerName = location.state?.customerName;
    const customerPhone = location.state?.customerPhone;
    const conversationStatus = location.state?.conversationStatus;
    const profilePicUrl = location.state?.profilePicUrl;
    
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [viewMode, setViewMode] = useState("chat"); // "chat" ou "table"
    const [newMessage, setNewMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(() => {
        const savedPreference = window.localStorage.getItem("whatsapp-chat-auto-scroll");
        return savedPreference === null ? true : savedPreference === "true";
    });
    const [isFullscreen, setIsFullscreen] = useState(false);
    
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const containerRef = useRef(null);

    const filters = useMemo(() => {
        if (conversationId) {
            return [{
                field: "conversation.id",
                value: conversationId,
                operation: "EQUAL"
            }];
        }
        return [];
    }, [conversationId]);

    // Scroll para a última mensagem
    const scrollToBottom = useCallback((behavior = "smooth") => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior });
        }
    }, []);

    // Carregar mensagens
    const loadMessages = useCallback(() => {
        setLoading(true);
        listMessageLogs(filters)
            .then((resp) => {
                // Ordenar por data (mais antiga primeiro para exibição de chat)
                const sortedLogs = (resp.data || []).sort((a, b) => {
                    if (!a.eventAt) return -1;
                    if (!b.eventAt) return 1;
                    return new Date(a.eventAt) - new Date(b.eventAt);
                });
                setLogs(sortedLogs);
            })
            .catch((error) => errorAlert(error))
            .finally(() => setLoading(false));
    }, [filters]);

    useEffect(() => {
        loadMessages();
    }, [loadMessages]);

    useEffect(() => {
        window.localStorage.setItem("whatsapp-chat-auto-scroll", String(autoScrollEnabled));
    }, [autoScrollEnabled]);

    // Scroll automático quando mensagens carregam ou são adicionadas
    useEffect(() => {
        if (autoScrollEnabled && logs.length > 0 && viewMode === "chat") {
            // Usar timeout para garantir que o DOM foi atualizado
            setTimeout(() => scrollToBottom("auto"), 100);
        }
    }, [autoScrollEnabled, logs, viewMode, scrollToBottom]);

    // Polling para novas mensagens
    useEffect(() => {
        const interval = setInterval(() => {
            loadMessages();
        }, 10000);

        return () => clearInterval(interval);
    }, [loadMessages]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    }, []);

    const handleSendMessage = useCallback(() => {
        const trimmed = newMessage.trim();
        if (!trimmed || sending) return;
        setSending(true);
        sendConversationMessage(conversationId, trimmed)
            .then(() => {
                setNewMessage("");
                loadMessages();
            })
            .catch((error) => errorAlert(error))
            .finally(() => setSending(false));
    }, [newMessage, sending, conversationId, loadMessages]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    }, [handleSendMessage]);

    const handleToggleAutoScroll = useCallback(() => {
        setAutoScrollEnabled((previousValue) => !previousValue);
    }, []);

    const handleToggleFullscreen = useCallback(async () => {
        if (!containerRef.current || !document.fullscreenEnabled) {
            return;
        }

        try {
            if (document.fullscreenElement === containerRef.current) {
                await document.exitFullscreen();
            } else {
                await containerRef.current.requestFullscreen();
            }
        } catch {
            // Silenciosamente ignora falhas da Fullscreen API
        }
    }, []);

    const stats = useMemo(() => {
        const inbound = logs.filter((log) => log.direction === "INBOUND").length;
        const outbound = logs.filter((log) => log.direction === "OUTBOUND").length;

        return {
            inbound,
            outbound,
            total: logs.length
        };
    }, [logs]);

    // Agrupar mensagens por data
    const groupedMessages = useMemo(() => {
        const groups = {};
        logs.forEach((log) => {
            if (!log.eventAt) return;
            const date = new Date(log.eventAt).toLocaleDateString('pt-BR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(log);
        });
        return groups;
    }, [logs]);

    const parsePayload = (payload) => {
        if (!payload) return '';
        if (typeof payload === 'object') {
            return JSON.stringify(payload, null, 2);
        }
        try {
            return JSON.stringify(JSON.parse(payload), null, 2);
        } catch {
            return payload;
        }
    };

    const getMessageMediaUrl = (log) => {
        const type = log.messageType?.toLowerCase();
        const payloadObject = getPayloadObject(log.payload);
        return payloadObject?.[type]?.link
            || payloadObject?.[type]?.url
            || payloadObject?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.[type]?.link
            || payloadObject?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.[type]?.url
            || null;
    };

    const getMessageTypeIcon = (type) => {
        const icons = {
            text: "fa-comment",
            image: "fa-image",
            audio: "fa-microphone",
            document: "fa-file-alt",
            video: "fa-video",
            sticker: "fa-sticky-note",
            location: "fa-map-marker-alt",
            contact: "fa-address-card"
        };
        return icons[type?.toLowerCase()] || "fa-comment";
    };

    const renderMessageContent = (log) => {
        const type = log.messageType?.toLowerCase();
        const messageBody = getMessageBody(log);
        const mediaUrl = getMessageMediaUrl(log);
        
        if (type === "image" && mediaUrl) {
            return (
                <div className="message-media">
                    <img src={mediaUrl} alt="Imagem" className="message-image" />
                    {messageBody && messageBody !== "-" && <p className="message-caption">{messageBody}</p>}
                </div>
            );
        }
        
        if (type === "audio") {
            return (
                <AudioMessagePlayer log={log} t={t} />
            );
        }
        
        if (type === "document") {
            return (
                <div className="message-document">
                    <i className="fa fa-file-alt"></i>
                    <span>{messageBody || t('document')}</span>
                </div>
            );
        }

        // Texto padrão
        return (
            <p className="message-text">{messageBody}</p>
        );
    };

    const formatMessageTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <>
            <PageTitle activeMenu={t('whatsapp_message_logs')} motherMenu={t('chat_bot')} />
            
            <div
                ref={containerRef}
                className={`whatsapp-chat-container ${isFullscreen ? "is-fullscreen" : ""}`}
            >
                {/* Header do chat */}
                <div className="chat-header">
                    <div className="chat-header-left">
                        <button className="back-button" onClick={() => navigate(-1)}>
                            <i className="fa fa-arrow-left"></i>
                        </button>
                        <div className="chat-avatar" style={{ background: profilePicUrl ? 'transparent' : '#25d366' }}>
                            {profilePicUrl ? (
                                <img
                                    src={profilePicUrl}
                                    alt={customerName || ""}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                                    onError={(e) => {
                                        e.currentTarget.parentElement.style.background = '#25d366';
                                        e.currentTarget.style.display = 'none';
                                        const icon = document.createElement('i');
                                        icon.className = 'fab fa-whatsapp';
                                        e.currentTarget.parentElement.appendChild(icon);
                                    }}
                                />
                            ) : (
                                <i className="fab fa-whatsapp"></i>
                            )}
                        </div>
                        <div className="chat-info">
                            <h4 className="chat-name">
                                {customerName || formatPhoneNumberOfPhoneComponent(customerPhone) || t('conversation')}
                            </h4>
                            <span className="chat-status">
                                {stats.total} {t('messages').toLowerCase()} • {stats.inbound} {t('received').toLowerCase()}, {stats.outbound} {t('sent').toLowerCase()}
                            </span>
                        </div>
                    </div>
                    <div className="chat-header-right">
                        <div className="view-toggle">
                            <button 
                                className={`toggle-btn ${viewMode === "chat" ? "active" : ""}`}
                                onClick={() => setViewMode("chat")}
                                title={t('chat_view')}
                            >
                                <i className="fa fa-comments"></i>
                            </button>
                            <button 
                                className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
                                onClick={() => setViewMode("table")}
                                title={t('table_view')}
                            >
                                <i className="fa fa-table"></i>
                            </button>
                        </div>
                        <button
                            className={`fullscreen-toggle ${isFullscreen ? "active" : ""}`}
                            onClick={handleToggleFullscreen}
                            title={isFullscreen ? t('exit_fullscreen') : t('enter_fullscreen')}
                        >
                            <i className={`fa ${isFullscreen ? "fa-compress" : "fa-expand"}`}></i>
                        </button>
                        <button
                            className={`auto-scroll-toggle ${autoScrollEnabled ? "active" : ""}`}
                            onClick={handleToggleAutoScroll}
                            title={autoScrollEnabled ? t('auto_scroll_enabled') : t('auto_scroll_disabled')}
                        >
                            <i className={`fa ${autoScrollEnabled ? "fa-magic" : "fa-hand-paper"}`}></i>
                            <span>{t('auto_scroll')}</span>
                        </button>
                        <button className="refresh-button" onClick={loadMessages} disabled={loading}>
                            <i className={`fa fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>
                        </button>
                    </div>
                </div>

                {/* Área de mensagens */}
                {viewMode === "chat" ? (
                    <div className="chat-messages" ref={chatContainerRef}>
                        {loading && logs.length === 0 ? (
                            <div className="chat-loading">
                                <Spinner animation="border" variant="success" />
                                <span>{t('loading_messages')}...</span>
                            </div>
                        ) : logs.length === 0 ? (
                            <div className="chat-empty">
                                <i className="fa fa-comments"></i>
                                <span>{t('no_messages_yet')}</span>
                            </div>
                        ) : (
                            <>
                                {Object.entries(groupedMessages).map(([date, messages]) => (
                                    <div key={date} className="message-group">
                                        <div className="date-separator">
                                            <span>{date}</span>
                                        </div>
                                        {messages.map((log) => {
                                            const isOutbound = log.direction === "OUTBOUND";
                                            const isSelected = selectedMessage?.id === log.id;
                                            
                                            return (
                                                <div 
                                                    key={log.id} 
                                                    className={`message-wrapper ${isOutbound ? "outbound" : "inbound"}`}
                                                >
                                                    <OverlayTrigger
                                                        placement={isOutbound ? "left" : "right"}
                                                        overlay={
                                                            <Tooltip>
                                                                {formatDateTime(log.eventAt)}
                                                                <br />
                                                                {isOutbound ? t('sent_to') : t('received_from')}: {formatPhoneNumberOfPhoneComponent(isOutbound ? log.toNumber : log.fromNumber)}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <div 
                                                            className={`message-bubble ${isSelected ? "selected" : ""}`}
                                                            onClick={() => setSelectedMessage(isSelected ? null : log)}
                                                        >
                                                            {log.messageType && log.messageType !== "TEXT" && (
                                                                <span className="message-type-badge">
                                                                    <i className={`fa ${getMessageTypeIcon(log.messageType)}`}></i>
                                                                </span>
                                                            )}
                                                            {renderMessageContent(log)}
                                                            <div className="message-meta">
                                                                <span className="message-time">
                                                                    {formatMessageTime(log.eventAt)}
                                                                </span>
                                                                {isOutbound && (
                                                                    <span className="message-status">
                                                                        {log.payload?.status === "read" ? (
                                                                            <i className="fa fa-check-double text-info"></i>
                                                                        ) : log.payload?.status === "delivered" ? (
                                                                            <i className="fa fa-check-double"></i>
                                                                        ) : (
                                                                            <i className="fa fa-check"></i>
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </OverlayTrigger>
                                                    
                                                    {/* Painel de detalhes expandido */}
                                                    {isSelected && (
                                                        <div className="message-details">
                                                            <div className="details-header">
                                                                <span>{t('message_details')}</span>
                                                                <button onClick={() => setSelectedMessage(null)}>
                                                                    <i className="fa fa-times"></i>
                                                                </button>
                                                            </div>
                                                            <div className="details-content">
                                                                <div className="detail-row">
                                                                    <span className="detail-label">{t('direction')}:</span>
                                                                    <span className={`detail-value direction-${log.direction?.toLowerCase()}`}>
                                                                        {log.direction === "OUTBOUND" ? t('outbound') : t('inbound')}
                                                                    </span>
                                                                </div>
                                                                <div className="detail-row">
                                                                    <span className="detail-label">{t('from_number')}:</span>
                                                                    <span className="detail-value">{formatPhoneNumberOfPhoneComponent(log.fromNumber)}</span>
                                                                </div>
                                                                <div className="detail-row">
                                                                    <span className="detail-label">{t('to_number')}:</span>
                                                                    <span className="detail-value">{formatPhoneNumberOfPhoneComponent(log.toNumber)}</span>
                                                                </div>
                                                                <div className="detail-row">
                                                                    <span className="detail-label">{t('message_type')}:</span>
                                                                    <span className="detail-value">{log.messageType || '-'}</span>
                                                                </div>
                                                                {log.payload && (
                                                                    <div className="detail-payload">
                                                                        <span className="detail-label">{t('payload')}:</span>
                                                                        <pre>{parsePayload(log.payload)}</pre>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </div>
                ) : (
                    /* Modo tabela */
                    <div className="chat-table-view">
                        <table className="messages-table">
                            <thead>
                                <tr>
                                    <th>{t('direction')}</th>
                                    <th>{t('message_type')}</th>
                                    <th>{t('body_preview')}</th>
                                    <th>{t('from_number')}</th>
                                    <th>{t('to_number')}</th>
                                    <th>{t('event_at')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...logs].reverse().map((log) => (
                                    <tr key={log.id} className={log.direction === "OUTBOUND" ? "outbound-row" : "inbound-row"}>
                                        <td>
                                            <span className={`direction-badge ${log.direction?.toLowerCase()}`}>
                                                {log.direction === "OUTBOUND" ? t('outbound') : t('inbound')}
                                            </span>
                                        </td>
                                        <td>
                                            <Badge bg="secondary" className="type-badge">
                                                <i className={`fa ${getMessageTypeIcon(log.messageType)} me-1`}></i>
                                                {log.messageType || '-'}
                                            </Badge>
                                        </td>
                                        <td className="message-cell">
                                            <span className="message-preview">{getMessageBody(log)}</span>
                                        </td>
                                        <td>{formatPhoneNumberOfPhoneComponent(log.fromNumber)}</td>
                                        <td>{formatPhoneNumberOfPhoneComponent(log.toNumber)}</td>
                                        <td>
                                            <span className="date-value">{formatDateTime(log.eventAt)}</span>
                                            <span className="time-ago">{formatTimeAgo(log.eventAt)}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Botão de scroll para baixo */}
                {viewMode === "chat" && logs.length > 10 && (
                    <button 
                        className="scroll-to-bottom"
                        onClick={() => scrollToBottom()}
                        title={t('scroll_to_bottom')}
                    >
                        <i className="fa fa-chevron-down"></i>
                    </button>
                )}

                {/* Campo de envio de mensagem - apenas quando em atendimento */}
                {conversationStatus === "IN_ATTENDANCE" && viewMode === "chat" && (
                    <div className="chat-input-area">
                        <textarea
                            className="chat-input"
                            rows={1}
                            placeholder={t('type_message') || "Digite uma mensagem..."}
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={sending}
                        />
                        <button
                            className="chat-send-btn"
                            onClick={handleSendMessage}
                            disabled={sending || !newMessage.trim()}
                            title={t('send') || "Enviar"}
                        >
                            {sending
                                ? <i className="fa fa-spinner fa-spin"></i>
                                : <i className="fa fa-paper-plane"></i>
                            }
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default WhatsappMessageLogList;
